import { supabase } from '../supabase/supabaseClient';
import { getStockQuote, searchStock } from '../stock/stockService';

export interface Artifact {
  id: string;
  group_id: string;
  ticker: string;
  asset_type: 'stock' | 'crypto' | 'forex' | 'commodity' | 'index';
  name: string;
  created_at: string;
  last_discussed_at: string;
  message_count: number;
  metadata: {
    current_price?: number;
    price_change_percent?: number;
    is_ai_recommended?: boolean;
  } | null;
}

/**
 * Get all artifacts (stocks/crypto) discussed in a group
 */
export const getGroupArtifacts = async (
  groupId: string
): Promise<{ data: Artifact[] | null; error: string | null }> => {
  try {
    console.log(`🎨 [ArtifactService] Fetching artifacts for group: ${groupId}`);
    const startTime = Date.now();

    const { data: artifacts, error } = await supabase
      .from('artifacts')
      .select('*')
      .eq('group_id', groupId)
      .order('last_discussed_at', { ascending: false });

    if (error) {
      console.error(`❌ [ArtifactService] Error fetching artifacts for group ${groupId}:`, error);
      return { data: null, error: error.message };
    }

    const fetchTime = Date.now() - startTime;
    console.log(
      `✅ [ArtifactService] Fetched ${artifacts?.length || 0} artifacts in ${fetchTime}ms`
    );

    if (artifacts && artifacts.length > 0) {
      const tickers = artifacts.map(a => a.ticker).join(', ');
      console.log(`📊 [ArtifactService] Artifacts: ${tickers}`);
    }

    return { data: artifacts || [], error: null };
  } catch (error) {
    console.error(
      `❌ [ArtifactService] Unexpected error fetching artifacts for group ${groupId}:`,
      error
    );
    return { data: null, error: 'An unexpected error occurred' };
  }
};

/**
 * Create or update an artifact (auto-called when ticker is detected)
 */
export const createOrUpdateArtifact = async (
  groupId: string,
  ticker: string,
  assetType: 'stock' | 'crypto' | 'forex' | 'commodity' | 'index' = 'stock',
  options?: { is_ai_recommended?: boolean }
): Promise<{ data: Artifact | null; error: string | null }> => {
  try {
    console.log(
      `🎯 [ArtifactService] Creating/updating artifact: ${ticker} (${assetType}) for group ${groupId}`
    );

    // Check if artifact already exists
    const { data: existing } = await supabase
      .from('artifacts')
      .select('*')
      .eq('group_id', groupId)
      .eq('ticker', ticker)
      .single();

    if (existing) {
      console.log(
        `📝 [ArtifactService] Artifact ${ticker} exists - updating (current count: ${existing.message_count})`
      );

      // Update last_discussed_at and increment message_count
      const { data: updated, error: updateError } = await supabase
        .from('artifacts')
        .update({
          last_discussed_at: new Date().toISOString(),
          message_count: existing.message_count + 1,
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (updateError) {
        console.error(`❌ [ArtifactService] Error updating artifact ${ticker}:`, updateError);
        return { data: null, error: updateError.message };
      }

      console.log(
        `✅ [ArtifactService] Updated artifact ${ticker} - new count: ${updated.message_count}`
      );
      return { data: updated, error: null };
    } else {
      console.log(`🆕 [ArtifactService] Creating new artifact for ${ticker}`);

      // Try to get real stock name
      let stockName = ticker;

      if (assetType === 'stock') {
        console.log(`🔍 [ArtifactService] Fetching stock name for ${ticker}...`);
        try {
          const { data: searchResults } = await searchStock(ticker);
          if (searchResults && searchResults.length > 0) {
            stockName = searchResults[0]['2. name'] || ticker;
            console.log(`✅ [ArtifactService] Found name for ${ticker}: ${stockName}`);
          }
        } catch (err) {
          console.warn(
            `⚠️ [ArtifactService] Failed to fetch name for ${ticker}, using ticker as name`
          );
        }
      }

      // Create new artifact
      const { data: created, error: createError } = await supabase
        .from('artifacts')
        .insert({
          group_id: groupId,
          ticker,
          asset_type: assetType,
          name: stockName, // Now uses real name if available
          message_count: 1,
          metadata: {
            is_ai_recommended: options?.is_ai_recommended || false,
          },
        })
        .select()
        .single();

      if (createError) {
        console.error(`❌ [ArtifactService] Error creating artifact ${ticker}:`, createError);
        return { data: null, error: createError.message };
      }

      const aiFlag = options?.is_ai_recommended ? ' ⚡ (AI-recommended)' : '';
      console.log(`✅ [ArtifactService] Created new artifact: ${ticker} (${stockName})${aiFlag}`);
      return { data: created, error: null };
    }
  } catch (error) {
    console.error(`❌ [ArtifactService] Unexpected error with artifact ${ticker}:`, error);
    return { data: null, error: 'An unexpected error occurred' };
  }
};

/**
 * Update artifact with current stock price
 */
export const updateArtifactPrice = async (
  artifactId: string,
  ticker: string
): Promise<{ success: boolean; error: string | null }> => {
  try {
    console.log(`💰 [ArtifactService] Updating price for ${ticker} (ID: ${artifactId})`);

    // Fetch current price
    const { data: quote, error } = await getStockQuote(ticker);

    if (error || !quote) {
      console.error(`❌ [ArtifactService] Failed to fetch price for ${ticker}:`, error);
      return { success: false, error: error || 'Failed to fetch quote' };
    }

    console.log(
      `📈 [ArtifactService] Fetched price for ${ticker}: $${quote.price} (${quote.changePercent >= 0 ? '+' : ''}${quote.changePercent.toFixed(2)}%)`
    );

    // Preserve existing metadata (do not clobber is_ai_recommended)
    const { data: existingRow } = await supabase
      .from('artifacts')
      .select('metadata')
      .eq('id', artifactId)
      .single();

    const existingMeta = (existingRow?.metadata as any) || {};
    const mergedMeta = {
      ...existingMeta,
      current_price: quote.price,
      price_change_percent: quote.changePercent,
      last_updated: new Date().toISOString(),
    };

    // Update artifact metadata (merge)
    const { error: updateError } = await supabase
      .from('artifacts')
      .update({ metadata: mergedMeta })
      .eq('id', artifactId);

    if (updateError) {
      console.error(
        `❌ [ArtifactService] Error updating artifact price for ${ticker}:`,
        updateError
      );
      return { success: false, error: updateError.message };
    }

    console.log(`✅ [ArtifactService] Updated price for ${ticker}`);
    return { success: true, error: null };
  } catch (error) {
    console.error(`❌ [ArtifactService] Unexpected error updating price for ${ticker}:`, error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

/**
 * Update prices for all artifacts in a group
 */
export const updateGroupArtifactPrices = async (
  groupId: string
): Promise<{ updated: number; failed: number }> => {
  try {
    console.log(`💵 [ArtifactService] Starting batch price update for group: ${groupId}`);
    const startTime = Date.now();

    const { data: artifacts } = await supabase
      .from('artifacts')
      .select('id, ticker, asset_type')
      .eq('group_id', groupId);

    if (!artifacts || artifacts.length === 0) {
      console.log(`ℹ️ [ArtifactService] No artifacts found for group ${groupId}`);
      return { updated: 0, failed: 0 };
    }

    console.log(`📊 [ArtifactService] Found ${artifacts.length} total artifacts`);

    let updated = 0;
    let failed = 0;

    // Update only stocks (skip crypto for now)
    const stockArtifacts = artifacts.filter(a => a.asset_type === 'stock');
    console.log(
      `📈 [ArtifactService] Updating ${stockArtifacts.length} stock artifacts (skipping ${artifacts.length - stockArtifacts.length} non-stocks)`
    );

    for (const artifact of stockArtifacts) {
      const { success } = await updateArtifactPrice(artifact.id, artifact.ticker);

      if (success) {
        updated++;
        console.log(`   ✅ [${updated}/${stockArtifacts.length}] ${artifact.ticker} updated`);
      } else {
        failed++;
        console.log(
          `   ❌ [${updated + failed}/${stockArtifacts.length}] ${artifact.ticker} failed`
        );
      }

      // Add delay to avoid rate limits (12 seconds between requests)
      if (updated + failed < stockArtifacts.length) {
        console.log(`   ⏳ Waiting 12s to avoid rate limits...`);
        await new Promise(resolve => setTimeout(resolve, 12000));
      }
    }

    const totalTime = Date.now() - startTime;
    console.log(
      `✅ [ArtifactService] Batch price update complete: ${updated} updated, ${failed} failed in ${(totalTime / 1000).toFixed(1)}s`
    );

    return { updated, failed };
  } catch (error) {
    console.error(
      `❌ [ArtifactService] Error updating group artifact prices for ${groupId}:`,
      error
    );
    return { updated: 0, failed: artifacts?.length || 0 };
  }
};

/**
 * Get message IDs for a specific ticker
 */
export const getArtifactMessages = async (
  groupId: string,
  ticker: string
): Promise<{ messageIds: string[]; error: string | null }> => {
  try {
    console.log(
      `🔍 [ArtifactService] Fetching messages for artifact ${ticker} in group ${groupId}`
    );

    // Get the artifact
    const { data: artifact } = await supabase
      .from('artifacts')
      .select('id')
      .eq('group_id', groupId)
      .eq('ticker', ticker)
      .single();

    if (!artifact) {
      console.log(`ℹ️ [ArtifactService] No artifact found for ${ticker} in group ${groupId}`);
      return { messageIds: [], error: null };
    }

    console.log(`✅ [ArtifactService] Found artifact ${ticker} (ID: ${artifact.id})`);

    // Get message IDs linked to this artifact
    const { data: links, error } = await supabase
      .from('message_artifacts')
      .select('message_id')
      .eq('artifact_id', artifact.id);

    if (error) {
      console.error(`❌ [ArtifactService] Error fetching messages for artifact ${ticker}:`, error);
      return { messageIds: [], error: error.message };
    }

    const messageIds = (links || []).map(link => link.message_id);
    console.log(`✅ [ArtifactService] Found ${messageIds.length} messages for artifact ${ticker}`);

    return {
      messageIds,
      error: null,
    };
  } catch (error) {
    console.error(
      `❌ [ArtifactService] Unexpected error fetching messages for artifact ${ticker}:`,
      error
    );
    return { messageIds: [], error: 'An unexpected error occurred' };
  }
};
